import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../auth/AuthContext'
import { AppShell } from '../components/AppShell'
import { schoolApi } from '../api/school'
import { queryKeys } from '../api/queryKeys'
import { NoSchool } from '../components/school/NoSchool'
import { SchoolInfo } from '../components/school/SchoolInfo'
import { SchoolDashboard } from '../components/school/SchoolDashboard'
import { CreateSchoolDialog } from '../components/school/CreateSchoolDialog'
import { InviteDialog } from '../components/school/InviteDialog'
import { SchoolLlmStats } from '../components/school/SchoolLlmStats'
import { SchoolSecuritySettings } from '../components/school/SchoolSecuritySettings'
import type { TFunction } from 'i18next'

const INVITE_ROLES = ['VICE_PRINCIPAL', 'PRINCIPAL', 'IT_ADMIN', 'ADMIN']
const SCHOOL_ADMINS = new Set(['VICE_PRINCIPAL', 'PRINCIPAL', 'ADMIN'])
/** Кто меняет политику 2FA школы (зеркало TwoFactorAdminService). */
const SECURITY_ADMINS = new Set(['PRINCIPAL', 'ADMIN', 'SUPER_ADMIN'])

/**
 * Школьный модуль: создание школы / приглашения учителей /
 * сводный список учителей с краткой статистикой.
 *
 * Сама страница — orchestrator: загружает данные школы, держит state
 * двух диалогов и решает, что показывать (NoSchool vs SchoolDashboard).
 * UI-кирпичики живут в `components/school/`.
 */
export function SchoolPage() {
  const { t } = useTranslation()
  const { teacher } = useAuth()
  const [showCreate, setShowCreate] = useState(false)
  const [showInvite, setShowInvite] = useState(false)
  const [joinToken, setJoinToken] = useState('')

  const schoolQuery = useQuery({
    queryKey: queryKeys.school.byId(teacher?.schoolId),
    queryFn: () => schoolApi.getSchool(teacher!.schoolId!),
    enabled: !!teacher?.schoolId,
    retry: 1,
  })

  if (!teacher) return null

  const hasSchool = !!teacher.schoolId
  const canInvite = INVITE_ROLES.includes(teacher.role)
  const school = schoolQuery.data

  return (
    <AppShell
      title={hasSchool ? (school?.name ?? t('schoolPage.title')) : t('schoolPage.title')}
      subtitle={pickSubtitle(hasSchool, school, t)}
    >
      {!hasSchool ? (
        <NoSchool
          onCreate={() => setShowCreate(true)}
          joinToken={joinToken}
          setJoinToken={setJoinToken}
        />
      ) : (
        <>
          {school && <SchoolInfo school={school} />}

          {school && SECURITY_ADMINS.has(teacher.role) && (
            <SchoolSecuritySettings school={school} />
          )}

          <a href="/app/billing" className="inline-block btn-outline text-sm mb-5">
            💳 {t('schoolPage.changeTariff')}
          </a>

          {SCHOOL_ADMINS.has(teacher.role) && (
            <SchoolLlmStats schoolId={teacher.schoolId!} />
          )}

          <SchoolDashboard
            schoolId={teacher.schoolId!}
            canInvite={canInvite}
            onInvite={() => setShowInvite(true)}
          />
        </>
      )}

      {showCreate && <CreateSchoolDialog onClose={() => setShowCreate(false)} />}
      {showInvite && hasSchool && (
        <InviteDialog schoolId={teacher.schoolId!} onClose={() => setShowInvite(false)} />
      )}
    </AppShell>
  )
}

function pickSubtitle(
  hasSchool: boolean,
  school: { shortName?: string | null; region?: string | null } | undefined,
  t: TFunction,
): string {
  if (!hasSchool) return t('schoolPage.subtitle')
  if (!school) return t('common.loading')
  const parts = [school.shortName, school.region].filter(Boolean)
  return parts.length > 0 ? parts.join(' · ') : t('schoolPage.subtitle')
}
